/**
 * TrustGate Console — Canonical Operator Flow Panel
 *
 * Descriptive-only projection of the canonical operator verb surface shipped in
 * `phase-c-cli-canonicalization-v1`. Renders the 6 canonical verbs in order
 * with a concise operator-facing description each, plus an informational
 * legacy → canonical mapping. This component performs NO execution, NO
 * navigation to execution surfaces, NO governance validation, and NO
 * authority decisions. It is a pure presentational projection and MUST stay
 * that way — see `docs/phase-c-ui-console-canonical-flows-v1.md`.
 */

import React from 'react';
import { Card } from './Card';
import { CANONICAL_FLOW_VERBS, LEGACY_CANONICAL_HINTS } from './canonicalFlow';

export const CanonicalFlowPanel: React.FC = () => {
  return (
    <Card title="Canonical Operator Flow" className="hover:shadow-xl transition-shadow duration-200">
      <div
        data-testid="canonical-flow-panel"
        className="space-y-4"
      >
        <p className="text-xs text-[#cbd5e1]">
          Preferred control-plane flow. Mirrors the Xable CLI canonical verbs
          shipped in <code className="text-[#00d4aa]">phase-c-cli-canonicalization-v1</code>.
          This panel is descriptive only and does not execute commands.
        </p>

        <ol
          data-testid="canonical-flow-list"
          className="space-y-2"
        >
          {CANONICAL_FLOW_VERBS.map((item, idx) => (
            <li
              key={item.verb}
              data-testid={`canonical-verb-${item.verb}`}
              className="flex items-start gap-3 p-2 bg-white/5 rounded-lg"
            >
              <span className="text-xs font-mono text-[#cbd5e1] mt-0.5 w-5 text-right">
                {idx + 1}.
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-[#f8fafc] font-mono">
                    {item.verb}
                  </span>
                  <span className="text-xs text-[#cbd5e1]">— {item.description}</span>
                </div>
                <div className="text-[10px] font-mono text-[#64748b] mt-0.5">
                  routes to: {item.routesTo}
                </div>
              </div>
            </li>
          ))}
        </ol>

        <div className="pt-2 border-t border-white/10">
          <div className="text-xs text-[#cbd5e1] mb-1">
            Legacy (still functional, deprecated as primary surface):
          </div>
          <ul
            data-testid="canonical-flow-legacy-list"
            className="space-y-1"
          >
            {LEGACY_CANONICAL_HINTS.map(({ legacy, canonical }) => (
              <li
                key={legacy}
                className="text-[11px] font-mono text-[#94a3b8]"
              >
                <span className="text-[#64748b]">{legacy}</span>
                <span className="px-1 text-[#64748b]">→</span>
                <span className="text-[#f8fafc]">{canonical}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
};
